import React from "react";
import { CloudRain, Sun, Droplets } from "lucide-react";
import { fmt, fmtInt } from "../../lib/format";

/**
 * Format a numeric reading with its unit, returning "—" for missing data
 * so the cell renders an honest empty state instead of a fabricated zero.
 */
function readingOrDash(value, formatter, unit) {
  if (value == null) return { text: "—", unit: null };
  return { text: formatter(value), unit };
}

function MiniInsight({ Icon, label, value, unit, accent, testid }) {
  return (
    <div className="card-base p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-2xl mt-1.5 font-semibold tabular-nums">
        {value}
        {unit && <span className="text-xs text-muted-foreground ml-1 font-normal">{unit}</span>}
      </div>
    </div>
  );
}

/**
 * Three-card "Today" strip showing the most recent day's deltas:
 *   • Rain gained
 *   • PV generated
 *   • Water consumed
 *
 * All values come from existing /energy/daily, /water/daily, and dashboard
 * endpoints — nothing inferred or AI-generated. Empty days render "—".
 */
export default function TodaysChange({ latestEnergy, latestWater, latestWeather }) {
  const rain = readingOrDash(latestWeather?.rain_mm, (n) => fmt(n, 1), "mm");
  const pv = readingOrDash(latestEnergy?.pv_production_kwh, (n) => fmt(n, 1), "kWh");
  const water = readingOrDash(latestWater?.consumption_liters, fmtInt, "L");

  return (
    <section className="space-y-3" data-testid="todays-change">
      <h2 className="label-tiny">Today&apos;s change</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MiniInsight Icon={CloudRain} label="Rain gained today" value={rain.text} unit={rain.unit} accent="#3B82F6" testid="today-rain" />
        <MiniInsight Icon={Sun}       label="PV generated today" value={pv.text} unit={pv.unit} accent="#F59E0B" testid="today-pv" />
        <MiniInsight Icon={Droplets}  label="Water consumed today" value={water.text} unit={water.unit} accent="#3B82F6" testid="today-water" />
      </div>
    </section>
  );
}
